import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppService } from 'src/app/services/app.service';
import { DeployService } from 'src/app/services/deploy.service';
import { IdleService } from 'src/app/services/idle.service';
import { WebSocketService } from 'src/app/services/web-socket.service';
import { environment } from 'src/environments/environment';
import { AccessRulesService } from '../../services/access-rules.service';
import { DocInsightsService } from '../../services/doc-insights.service';
import { UserRevokeService } from '../../services/general/user-revoke.service';
import { MetaService } from '../../services/meta.service';
import { ProfileService } from '../../services/profile.service';
import { StorageService } from '../../services/storage.service';

declare let YTFeedbackForm: any;

@Component({
  standalone: false,
  selector: 'cms-authorization',
  templateUrl: './authorization.component.html',
  styleUrls: ['./authorization.component.scss'],
  providers: [UserRevokeService],
})
export class AuthorizationComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('ytWidgetContainer', { static: false }) ytWidgetContainer!: ElementRef;

  @ViewChild('docInsightsEl', { static: false }) docInsightsEl!: ElementRef;

  get widgetId(): string {
    return this.accessRulesService.getFeaturesRules().supportWidget.properties.WIDGET_ID;
  }

  get widgetBackendUrl(): string {
    return this.accessRulesService.getFeaturesRules().supportWidget.properties.WIDGET_URL;
  }

  private popupButtonMutationObserver!: MutationObserver;

  private closeButtonMutationObserver!: MutationObserver;

  private popupShownMutationObserver!: MutationObserver;

  private iconMutationObserver!: MutationObserver;

  private attachmentButtonMutationObserver!: MutationObserver;

  private formContentMutationObserver!: MutationObserver;

  private progressOverlayEl: HTMLElement | null = null;

  private isProgressVisible = false;

  private currentWidgetIconName = 'chat';

  private progressShownAt = 0;

  private static readonly MIN_PROGRESS_VISIBLE_MS = 400;

  private formApi: any;

  private widgetPosition = { x: 20, y: 20 };

  private hasSavedWidgetPosition = false;

  private isDragging = false;

  private dragOffset = { x: 0, y: 0 };

  private dragStartPosition = { x: 0, y: 0 };

  private hasMoved = false;

  private mouseDownTime = 0;

  private navigationInProgress = false;

  private destroy$ = new Subject<void>();
  configReady = false;

  // Doc Insights properties
  docInsightsOpen = false;

  docInsightsDocId: string | null = null;

  get devMode(): string | null {
    return !environment.production ? '' : null;
  }

  get docInsightsWidgetKey(): string {
    return this.accessRulesService.getFeaturesRules().plexteraDocumentInfo.properties.WIDGET_KEY;
  }

  get showDocInsights(): boolean {
    return this.accessRulesService.isFeatureEnabled(
      this.accessRulesService.getFeaturesRules().plexteraDocumentInfo.enable,
    );
  }

  constructor(
    private webSocketSvc: WebSocketService,
    private idleSvc: IdleService,
    private userRevokeService: UserRevokeService,
    private deploySvc: DeployService,
    private profileService: ProfileService,
    private accessRulesService: AccessRulesService,
    private renderer: Renderer2,
    private storageSvc: StorageService,
    private appSvc: AppService,
    private docInsightsService: DocInsightsService,
    private metaService: MetaService,
    private ngZone: NgZone,
  ) {}

  get showSupportWidget(): boolean {
    return this.accessRulesService.isFeatureEnabled(
      this.accessRulesService.getFeaturesRules().supportWidget.enable,
    );
  }

  ngOnInit(): void {
    this.accessRulesService.initializeService().then(() => {
      (window as any).matomoSiteId =
        this.accessRulesService.getFeaturesRules().matomoSiteId ??
        this.accessRulesService.getEnvironmentSettings().matomoSiteId;
      this.configReady = true;
      if (this.showSupportWidget) {
        setTimeout(() => this.initSupportWidget());
      }
    });
    this.checkRequireTotpGeneration();
    this.idleSvc.start();
    this.webSocketSvc.connect();
    this.userRevokeService.subscribe();
    this.deploySvc.startTracking();
    this.loadWidgetPosition();
    this.subscribeToDocInsights();
  }

  private subscribeToDocInsights(): void {
    this.docInsightsService.state$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      this.docInsightsOpen = state.isOpen;
      this.docInsightsDocId = state.docId;
      setTimeout(() => {
        const el = this.docInsightsEl?.nativeElement;
        if (el) {
          el.open = state.isOpen;
          if (state.isOpen) {
            el.setAttribute('open', '');
          } else {
            el.removeAttribute('open');
          }
        }
      });
    });
  }

  ngAfterViewInit(): void {
    if (this.showSupportWidget) {
      this.attachPopupButtonClickListener();
    }
  }

  private initSupportWidget(): void {
    if (!(window as any).YTFeedbackForm) {
      this.loadScript(
        'https://plextera.youtrack.cloud/static/simplified/form/form-entry.js?auto=false',
      )
        .then(() => {
          this.initializeWidget();
          this.initializeDragAndDrop();
          this.attachClosePopupButton();
          this.addPersistentButtonOverlay();
        })
        .catch((error) => console.error(error));
    } else {
      this.initializeWidget();
      this.initializeDragAndDrop();
      this.attachClosePopupButton();
      this.addPersistentButtonOverlay();
    }
  }

  ngOnDestroy(): void {
    this.idleSvc.stop();
    this.userRevokeService.unsubscribe();
    this.deploySvc.stopTracking();
    this.webSocketSvc.close();
    if (this.closeButtonMutationObserver) {
      this.closeButtonMutationObserver.disconnect();
    }
    if (this.popupShownMutationObserver) {
      this.popupShownMutationObserver.disconnect();
    }
    if (this.iconMutationObserver) {
      this.iconMutationObserver.disconnect();
    }
    if (this.attachmentButtonMutationObserver) {
      this.attachmentButtonMutationObserver.disconnect();
    }
    if (this.formContentMutationObserver) {
      this.formContentMutationObserver.disconnect();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleDocInsightsClose(): void {
    this.docInsightsService.close();
  }

  get showRouterOutlet() {
    return !this.navigationInProgress;
  }

  private checkRequireTotpGeneration() {
    if (!this.profileService.user.requireTotpGeneration) {
      return;
    }

    this.navigationInProgress = true;

    this.appSvc.goToUserSettings().then(() => {
      this.navigationInProgress = false;
    });
  }

  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Script load error for ${src}`));
      document.body.appendChild(script);
    });
  }

  private initializeWidget(): void {
    YTFeedbackForm.renderFeedbackButton(this.ytWidgetContainer.nativeElement, {
      backendURL: this.widgetBackendUrl,
      formUUID: this.widgetId,
      theme: 'light',
      language: 'en',
    });

    YTFeedbackForm.getClientJSApi(this.widgetId).then((form: any) => {
      this.formApi = form;
      this.setFormValues();
    });
  }

  private setFormValues(): void {
    if (this.formApi) {
      this.formApi.setBlockValue('Email address', this.profileService.user.email);
      this.formApi.setBlockValue('Contact Name', this.profileService.user.name);
      this.formApi.setBlockValue('Client', this.accessRulesService.getProjectName());
      this.formApi.setBlockValue('Site Url', window.location.href);
    }
  }

  // The widget's own React instance stays mounted between opens (only visibility
  // toggles), so the user's own input has to be cleared explicitly on close
  private resetFormFields(): void {
    if (this.formApi) {
      this.formApi.setBlockValue('summary', '');
      this.formApi.setBlockValue('description', '');
    }
  }

  // "Submit Another Request" rebuilds the form's heading/inputs from scratch
  // (not just clearing them), which wipes our injected close button and
  // detaches the formApi from anything still rendered — rather than guessing
  // when that swap happens, keep checking the popup's content for a heading
  // that's missing our close button and re-patch it whenever that occurs
  private ensurePopupPatched(popupElement: HTMLElement): void {
    const heading = popupElement.querySelector('[data-test="heading"]');
    if (!heading || heading.querySelector('.cms-widget-close-btn')) return;

    this.injectClosePopupButton(popupElement);
    this.attachRemoveAttachmentFix(popupElement);
    YTFeedbackForm.getClientJSApi(this.widgetId).then((form: any) => {
      this.formApi = form;
      this.setFormValues();
    });
  }

  private attachPopupButtonClickListener(): void {
    this.ngZone.runOutsideAngular(() => {
      this.popupButtonMutationObserver = new MutationObserver((mutations, observer) => {
        const popupButton = document.querySelector('button[data-test="popup-button"]');
        if (popupButton) {
          this.renderer.listen(popupButton, 'click', () => {
            setTimeout(() => {
              this.setFormValues();
            }, 50);
          });
          observer.disconnect();
        }
      });
      this.popupButtonMutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    });
  }

  private addPersistentButtonOverlay(): void {
    const container = this.ytWidgetContainer?.nativeElement;
    if (!container) return;

    this.ngZone.runOutsideAngular(() => this.addPersistentButtonOverlayOutsideAngular(container));
  }

  // The widget's own React tree wipes any DOM nodes we inject whenever the
  // button's contents re-render (e.g. on open/close), so this needs to keep
  // reapplying indefinitely rather than injecting once
  private addPersistentButtonOverlayOutsideAngular(container: HTMLElement): void {
    const applyOverlay = (button: HTMLElement) => {
      const icon = button.querySelector('.feedback-popup-button-icon');
      if (icon && !icon.querySelector('.material-icons')) {
        icon.innerHTML = '';
        const materialIcon = this.renderer.createElement('i');
        this.renderer.addClass(materialIcon, 'material-icons');
        this.renderer.appendChild(materialIcon, this.renderer.createText(this.currentWidgetIconName));
        this.renderer.appendChild(icon, materialIcon);
      }

      let wave = button.querySelector('.cms-widget-progress') as HTMLElement | null;
      if (!wave) {
        wave = this.renderer.createElement('div');
        this.renderer.addClass(wave, 'cms-widget-progress');
        for (let i = 0; i < 5; i += 1) {
          const bar = this.renderer.createElement('div');
          this.renderer.addClass(bar, 'cms-widget-progress-bar');
          this.renderer.appendChild(wave, bar);
        }
        this.renderer.appendChild(button, wave);
      }
      this.progressOverlayEl = wave;
      // React can recreate this node while the popup is opening, so reassert
      // visibility every time based on our own tracked state, not the node's class
      if (this.isProgressVisible) {
        this.renderer.addClass(wave, 'cms-widget-progress-visible');
      }

      if (!button.hasAttribute('data-cms-click-bound')) {
        this.renderer.setAttribute(button, 'data-cms-click-bound', 'true');
        this.renderer.listen(button, 'click', () => {
          const popupElement = document.querySelector('div[data-test="ring-popup form-popup"]');
          const isCurrentlyOpen = popupElement?.getAttribute('data-test-shown') === 'true';
          if (isCurrentlyOpen) return;

          this.isProgressVisible = true;
          this.progressShownAt = Date.now();
          if (this.progressOverlayEl) {
            this.renderer.addClass(this.progressOverlayEl, 'cms-widget-progress-visible');
          }
          // Safety net in case the popup never appears (e.g. network error)
          setTimeout(() => this.hideProgressOverlay(), 5000);
        });
      }
    };

    const tryApply = () => {
      const button = container.querySelector('.feedback-popup-button');
      if (button) {
        applyOverlay(button as HTMLElement);
      }
    };

    tryApply();

    this.iconMutationObserver = new MutationObserver(() => tryApply());
    this.iconMutationObserver.observe(container, { childList: true, subtree: true });
  }

  private setWidgetIcon(name: string): void {
    this.currentWidgetIconName = name;
    const container = this.ytWidgetContainer?.nativeElement;
    const materialIcon = container?.querySelector('.feedback-popup-button-icon .material-icons');
    if (materialIcon) {
      materialIcon.textContent = name;
    }
  }

  private hideProgressOverlay(): void {
    if (!this.isProgressVisible) return;

    const remaining =
      AuthorizationComponent.MIN_PROGRESS_VISIBLE_MS - (Date.now() - this.progressShownAt);
    if (remaining > 0) {
      setTimeout(() => this.hideProgressOverlay(), remaining);
      return;
    }

    this.isProgressVisible = false;
    if (this.progressOverlayEl) {
      this.renderer.removeClass(this.progressOverlayEl, 'cms-widget-progress-visible');
    }
  }

  private attachClosePopupButton(): void {
    this.ngZone.runOutsideAngular(() => {
      this.closeButtonMutationObserver = new MutationObserver((mutations, obs) => {
        const popupElement = document.querySelector('div[data-test="ring-popup form-popup"]');
        if (popupElement) {
          obs.disconnect();
          this.waitForFormHeading(popupElement as HTMLElement, () => {
            this.hideProgressOverlay();
            this.setWidgetIcon('close');
            this.ensurePopupPatched(popupElement as HTMLElement);
          });

          this.formContentMutationObserver = new MutationObserver(() =>
            this.ensurePopupPatched(popupElement as HTMLElement),
          );
          this.formContentMutationObserver.observe(popupElement as HTMLElement, {
            childList: true,
            subtree: true,
          });
        }
      });

      this.closeButtonMutationObserver.observe(document.body, { childList: true, subtree: true });

      // The popup node is reused on subsequent opens (only this attribute
      // toggles), so watch it separately to hide the overlay/swap the icon every time
      this.popupShownMutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          const target = mutation.target as HTMLElement;
          if (!target.matches('div[data-test="ring-popup form-popup"]')) return;

          if (target.getAttribute('data-test-shown') === 'true') {
            this.waitForFormHeading(target, () => {
              this.hideProgressOverlay();
              this.setWidgetIcon('close');
              this.setFormValues();
            });
          } else {
            this.setWidgetIcon('chat');
            this.resetFormFields();
          }
        });
      });
      this.popupShownMutationObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['data-test-shown'],
        subtree: true,
      });
    });
  }

  // The heading only appears once the form's own async chunk/translations
  // have finished loading, so wait for it rather than guessing a fixed delay
  private waitForFormHeading(popupElement: HTMLElement, callback: () => void): void {
    if (popupElement.querySelector('[data-test="heading"]')) {
      callback();
      return;
    }

    const headingObserver = new MutationObserver((mutations, obs) => {
      if (popupElement.querySelector('[data-test="heading"]')) {
        obs.disconnect();
        callback();
      }
    });
    headingObserver.observe(popupElement, { childList: true, subtree: true });
  }

  // The widget's own "Remove attachment" button renders but is effectively
  // invisible/unusable in our narrow popup layout, so overlay a visible
  // replacement that just triggers the real (hidden) button's click handler.
  // Non-image attachments wrap it in a span tagged
  // data-test="ring-tooltip attachment-link", but image attachments use a
  // differently-tagged overlay (data-test="ring-tooltip" only) — so locate
  // tiles from the remove button itself instead of that inconsistent tag
  private attachRemoveAttachmentFix(popupElement: HTMLElement): void {
    const applyFix = () => {
      const nativeRemoveBtns = popupElement.querySelectorAll(
        'button[aria-label="Remove attachment"]',
      );
      nativeRemoveBtns.forEach((nativeRemoveBtnEl) => {
        const nativeRemoveBtn = nativeRemoveBtnEl as HTMLElement;
        const chip = nativeRemoveBtn.closest('.c_attachment__cfc') as HTMLElement;
        if (!chip || chip.querySelector('.cms-widget-remove-attachment')) return;

        this.renderer.setStyle(chip, 'position', 'relative');

        const removeBtn = this.renderer.createElement('button');
        this.renderer.setAttribute(removeBtn, 'type', 'button');
        this.renderer.setAttribute(removeBtn, 'class', 'cms-widget-remove-attachment');
        this.renderer.setAttribute(removeBtn, 'aria-label', 'Remove attachment');
        removeBtn.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" fill="none">' +
          '<path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
          '</svg>';

        this.renderer.listen(removeBtn, 'click', (event: Event) => {
          event.preventDefault();
          event.stopPropagation();
          nativeRemoveBtn.click();
        });

        this.renderer.appendChild(chip, removeBtn);
      });
    };

    applyFix();

    if (this.attachmentButtonMutationObserver) {
      this.attachmentButtonMutationObserver.disconnect();
    }
    this.attachmentButtonMutationObserver = new MutationObserver(() => applyFix());
    this.attachmentButtonMutationObserver.observe(popupElement, { childList: true, subtree: true });
  }

  private injectClosePopupButton(popupElement: HTMLElement): void {
    if (popupElement.querySelector('.cms-widget-close-btn')) return;

    const closeBtn = this.renderer.createElement('button');
    this.renderer.setAttribute(closeBtn, 'type', 'button');
    this.renderer.setAttribute(closeBtn, 'class', 'cms-widget-close-btn');
    this.renderer.setAttribute(closeBtn, 'aria-label', 'Close');
    closeBtn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" fill="none">' +
      '<path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
      '</svg>';

    this.renderer.listen(closeBtn, 'click', () => {
      const toggleBtn = document.querySelector('button[data-test="popup-button"]') as HTMLElement;
      if (!toggleBtn) return;

      ['pointerdown', 'mousedown', 'mouseup', 'pointerup', 'click'].forEach((eventType) => {
        const event =
          eventType === 'pointerdown' || eventType === 'pointerup'
            ? new PointerEvent(eventType, { bubbles: true })
            : new MouseEvent(eventType, { bubbles: true });
        toggleBtn.dispatchEvent(event);
      });
    });

    const heading = popupElement.querySelector('[data-test="heading"]');

    if (heading) {
      this.renderer.appendChild(heading, closeBtn);
    } else {
      const container = popupElement.querySelector('form') || popupElement;
      this.renderer.insertBefore(container, closeBtn, container.firstChild);
    }

    this.setFieldPlaceholder(popupElement, 'summary', 'input', 'Enter Summary');
    this.setFieldPlaceholder(popupElement, 'description', 'textarea', 'Provide More Details Here');
    this.colorizeRequiredAsterisks(popupElement);
  }

  // The "*" for required fields is a plain text node next to the label,
  // not its own element, so it can't be targeted with CSS alone
  private colorizeRequiredAsterisks(popupElement: HTMLElement): void {
    const labels = popupElement.querySelectorAll('[data-test="field-label"]');
    labels.forEach((label) => {
      const lastNode = label.lastChild;
      if (lastNode?.nodeType === Node.TEXT_NODE && lastNode.textContent?.trim() === '*') {
        const asterisk = this.renderer.createElement('span');
        this.renderer.addClass(asterisk, 'cms-widget-required-asterisk');
        this.renderer.appendChild(asterisk, this.renderer.createText('*'));
        this.renderer.removeChild(label, lastNode);
        this.renderer.appendChild(label, asterisk);
      }
    });
  }

  private setFieldPlaceholder(
    popupElement: HTMLElement,
    fieldTestId: string,
    inputTag: 'input' | 'textarea',
    placeholder: string,
  ): void {
    const selector = `[data-test="${fieldTestId}"] ${inputTag}`;
    const existingField = popupElement.querySelector(selector);
    if (existingField) {
      this.renderer.setAttribute(existingField, 'placeholder', placeholder);
      return;
    }

    const fieldObserver = new MutationObserver((mutations, obs) => {
      const field = popupElement.querySelector(selector);
      if (field) {
        this.renderer.setAttribute(field, 'placeholder', placeholder);
        obs.disconnect();
      }
    });
    fieldObserver.observe(popupElement, { childList: true, subtree: true });
  }

  private loadWidgetPosition(): void {
    const savedPosition = this.storageSvc.get('widgetPosition');
    if (
      savedPosition &&
      typeof savedPosition.x === 'number' &&
      typeof savedPosition.y === 'number' &&
      savedPosition.anchor === 'right'
    ) {
      this.widgetPosition = savedPosition;
      this.hasSavedWidgetPosition = true;
    } else {
      this.storageSvc.removeItem('widgetPosition');
      this.widgetPosition = { x: 20, y: 20 };
    }
  }

  private saveWidgetPosition(): void {
    this.storageSvc.set('widgetPosition', this.widgetPosition);
  }

  private initializeDragAndDrop(): void {
    if (!this.ytWidgetContainer) return;

    const container = this.ytWidgetContainer.nativeElement;

    // Apply saved position, defaulting to the left side of the screen
    if (this.hasSavedWidgetPosition) {
      this.renderer.setStyle(container, 'right', `${this.widgetPosition.x}px`);
      this.renderer.removeStyle(container, 'left');
    } else {
      this.renderer.setStyle(container, 'left', `${this.widgetPosition.x}px`);
      this.renderer.removeStyle(container, 'right');
    }
    this.renderer.setStyle(container, 'bottom', `${this.widgetPosition.y}px`);
    this.renderer.setStyle(container, 'cursor', 'grab');
    this.renderer.setStyle(container, 'user-select', 'none');

    // Mouse down - start tracking
    this.renderer.listen(container, 'mousedown', (event: MouseEvent) => {
      if (event.button === 0) {
        // Only left button
        this.onMouseDown(event);
      }
    });

    // Mouse move - drag if needed
    this.renderer.listen(document, 'mousemove', (event: MouseEvent) => {
      this.onMouseMove(event);
    });

    // Mouse up - stop everything
    this.renderer.listen(document, 'mouseup', (event: MouseEvent) => {
      this.onMouseUp(event);
    });

    // Prevent context menu
    this.renderer.listen(container, 'contextmenu', (event: Event) => {
      event.preventDefault();
    });

    // Block clicks after dragging
    this.renderer.listen(container, 'click', (event: MouseEvent) => {
      if (this.hasMoved) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
      }
    });
  }

  private onMouseDown(event: MouseEvent): void {
    this.mouseDownTime = Date.now();
    this.dragStartPosition = { x: event.clientX, y: event.clientY };
    this.isDragging = false;
    this.hasMoved = false;

    // Calculate offset for dragging
    const rect = this.ytWidgetContainer.nativeElement.getBoundingClientRect();
    this.dragOffset = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };

    // Prevent text selection
    event.preventDefault();
  }

  private onMouseMove(event: MouseEvent): void {
    if (this.mouseDownTime === 0) return; // Mouse not pressed

    const deltaX = Math.abs(event.clientX - this.dragStartPosition.x);
    const deltaY = Math.abs(event.clientY - this.dragStartPosition.y);
    const dragThreshold = 5;

    // Start dragging if moved enough
    if ((deltaX > dragThreshold || deltaY > dragThreshold) && !this.isDragging) {
      this.isDragging = true;
      this.hasMoved = true;
      this.renderer.setStyle(this.ytWidgetContainer.nativeElement, 'cursor', 'grabbing');
      this.renderer.removeStyle(this.ytWidgetContainer.nativeElement, 'left');
    }

    // Continue dragging if already started
    if (this.isDragging) {
      event.preventDefault();

      const containerEl = this.ytWidgetContainer.nativeElement;
      const newRight =
        window.innerWidth - (event.clientX - this.dragOffset.x) - containerEl.offsetWidth;
      const newY =
        window.innerHeight - (event.clientY - this.dragOffset.y + containerEl.offsetHeight);

      // Keep within bounds
      const minRight = 0;
      const maxRight = window.innerWidth - containerEl.offsetWidth;
      const minY = 0;
      const maxY = window.innerHeight - containerEl.offsetHeight;

      this.widgetPosition.x = Math.max(minRight, Math.min(maxRight, newRight));
      this.widgetPosition.y = Math.max(minY, Math.min(maxY, newY));

      // Apply position
      const container = this.ytWidgetContainer.nativeElement;
      this.renderer.setStyle(container, 'right', `${this.widgetPosition.x}px`);
      this.renderer.setStyle(container, 'bottom', `${this.widgetPosition.y}px`);
    }
  }

  private onMouseUp(event: MouseEvent): void {
    const wasMouseDown = this.mouseDownTime > 0;
    const wasDragging = this.isDragging;

    // Reset all states
    this.mouseDownTime = 0;
    this.isDragging = false;

    // Restore cursor
    if (this.ytWidgetContainer?.nativeElement) {
      this.renderer.setStyle(this.ytWidgetContainer.nativeElement, 'cursor', 'grab');
    }

    // Save position if was dragging
    if (wasDragging && this.hasMoved) {
      this.saveWidgetPosition();
    }

    // If it was just a click (no drag), allow normal widget behavior
    if (wasMouseDown && !this.hasMoved) {
      // This was a normal click - widget should open normally
      this.hasMoved = false;
      return;
    }

    // If it was a drag, prevent click events for a short time
    if (this.hasMoved) {
      setTimeout(() => {
        this.hasMoved = false;
      }, 150);
    }
  }
}
